import axios from "axios";
import { endOfDay, formatISO, startOfDay } from "date-fns";
import qs from "qs";
import { removeEmptyParams } from "app/shared/utils";
import { LOADER_TYPES } from "app/shared/constants";

const ACTIVITY_LOG_SORT_FIELDS = {
  timestamp: "timestamp",
  displayName: "userName",
  userName: "userName",
  role: "role",
  action: "operation",
  status: "status",
};

/**
 * Fetch activity log data from the backend API
 * @param {Function} setActivityLogs - Setter for activity log data
 * @param {Function} setTotalCount - Setter for total count
 * @param {Function} setAlert - Alert setter
 * @param {Object} filters - Filter object
 * @param {Object} pagination - Pagination object
 * @param {Object} sort - Sort object
 * @param {Function} setLoader - Loading state setter
 */
export const getAllActivityLogs = async (
  setActivityLogs,
  setTotalCount,
  setAlert,
  filters,
  pagination,
  sort,
  setLoader
) => {
  try {
    setLoader(LOADER_TYPES.ACTIVITY_LOG_DATA, true);

    const { page, rowsPerPage } = pagination;
    const { orderBy, order } = sort;
    const apiSortField = ACTIVITY_LOG_SORT_FIELDS[orderBy] || "timestamp";
    
    const activityLogParams = {
      startDate:
        filters.startDate && formatISO(startOfDay(new Date(filters.startDate))),
      endDate: filters.endDate && formatISO(endOfDay(new Date(filters.endDate))),
      users: filters.users?.length ? filters.users : undefined,
      actions: filters.actions?.length ? filters.actions : undefined,
      statuses: filters.statuses?.length ? filters.statuses : undefined,
      protocols: filters.protocols?.length ? filters.protocols : undefined,
      departments: filters.departments?.length ? filters.departments : undefined,
      nctIds: filters.nctIds?.length ? filters.nctIds : undefined,
      page: page,
      size: rowsPerPage,
      sort: `${apiSortField},${order}`,
    };

    removeEmptyParams(activityLogParams);

    const request = {
      url: `/activity-log`,
      params: activityLogParams,
      paramsSerializer: {
        serialize: (params) => qs.stringify(params, { arrayFormat: "repeat" }),
      },
    };

    const response = await axios(request);

    if (response.data && response.data.values) {
      setActivityLogs(
        response.data.values.map((activityLog) => ({
          ...activityLog,
          action: [activityLog.operation, activityLog.objectType].filter(Boolean).join(" "),
          data: activityLog.jsonData ?? activityLog.data ?? "",
        }))
      );
      setTotalCount(response.data.totalCount);
    } else {
      setActivityLogs([]);
      setTotalCount(0);
    }

    setLoader(LOADER_TYPES.ACTIVITY_LOG_DATA, false);
  } catch (error) {
    setLoader(LOADER_TYPES.ACTIVITY_LOG_DATA, false);
    setAlert("error", error.response?.data?.message || error.message || "Failed to load activity logs");
  }
};

/**
 * Fetch distinct filter values for dropdowns
 * @param {Function} setFilterOptions - Setter for filter options
 * @param {Function} setAlert - Alert setter
 */
export const getActivityLogFilterOptions = async (setFilterOptions, setAlert) => {
  try {
    const response = await axios({
      url: `/activity-log/filter-options`,
    });
    if (response.data) {
      setFilterOptions(response.data);
    }
  } catch (error) {
    setAlert(
      "error",
      error.response?.data?.message ||
        error.message ||
        "Failed to load Activity Log filter options"
    );
  }
};
